import axiosInstance from 'config/axiosInstance';
import { getSessionGeneration, getSessionIdentity } from 'config/session';

/**
 * EV-034（ADM-14 の AC1 の残り）。運転者の登録（POST /v1/drivers/）を、結果が分からない
 * 失敗の後も取り直せるようにする。
 *
 * - 登録の操作ごとに X-Request-ID を作る。サーバーは同じ値・同じ本文の再送に前回の応答を
 *   返す（docs/api/idempotency.md）。
 * - 結果が分からない失敗（応答なし・5xx・409 idempotency_in_progress）では、同じ値・同じ
 *   本文で1回送り直す。
 * - EV-041。処理中（409 idempotency_in_progress）の間は、同じ値・同じ本文で終わるまで
 *   （registerTiming.maxInProgressPolls 回まで）問い合わせ直す。処理中に一覧を読み直すと、
 *   招待が失敗して取り消される運転者が一時的に見えるため、結果が出るまで読み直さない。
 * - それでも分からなければ OutcomeUnknownError を投げる。呼び出し側は「確かめられ
 *   なかった」と示して入力を残し、一覧を読み直す。PR #104 レビュー（P2）。一覧を
 *   メールで照合して「登録できた」とはみなさない。同じメールの運転者が前からいても
 *   （大文字・小文字だけ違う場合も）、この要求が成功した証拠にはならないため。
 */

/**
 * 送り直すまでの待ち時間と、処理中（409 idempotency_in_progress）を問い合わせ直す上限の回数
 * （テストで短くできるよう、まとめておく）。処理中はサーバーが招待メールを送っている間
 * なので、上限は送信の時間制限より長くとる（既定で約20秒）。
 */
export const registerTiming = { retryDelayMs: 1000, maxInProgressPolls: 20 };

/**
 * 'created-without-invitation'（EV-085）: 後片付け待ちの再送で、運転者が使い始めていたため
 * サーバーが登録を残した（201 に invitation_sent: false）。招待メールは届いていない。
 */
export type RegisterOutcome = 'created' | 'created-without-invitation';

const outcomeOf = (response: any): RegisterOutcome =>
  response?.data?.invitation_sent === false ? 'created-without-invitation' : 'created';

/** 送り直しと照合でも、登録できたかを確かめられなかった。 */
export class OutcomeUnknownError extends Error {
  readonly cause: unknown;
  constructor(cause: unknown) {
    super('Could not confirm whether the driver was registered');
    this.name = 'OutcomeUnknownError';
    this.cause = cause;
  }
}

/**
 * EV-079。送り直しでも 502 registration_cleanup_pending が続いた。結果は分かっている:
 * 招待メールは届かず、取り消し（後片付け）が済んでいない登録がサーバーに残っている。
 * 時間をおいて同じ内容で登録し直すと、サーバーが先に取り消しをやり直す（EV-078）。
 */
export class CleanupPendingError extends Error {
  readonly cause: unknown;
  constructor(cause: unknown) {
    super('The invitation was not sent and the registration is not cancelled yet');
    this.name = 'CleanupPendingError';
    this.cause = cause;
  }
}

const isCleanupPending = (error: any) =>
  error?.response?.status === 502 && error.response.data?.code === 'registration_cleanup_pending';

/** UUID（使えなければ 32 文字の16進）。サーバーは 16〜64 文字の英数字とハイフンを受け付ける。 */
export function newRequestId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      return Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }
  } catch {
    /* 下の方法で作る */
  }
  let id = '';
  while (id.length < 32) id += Math.floor(Math.random() * 16).toString(16);
  return id;
}

/**
 * サーバーが処理したかが分からない失敗か。
 *
 * 通信の失敗（axios のエラーで応答なし）・408・5xx（502 email_not_sent を除く）・処理中の 409。別の利用者への
 * 切り替え（SessionChangedError）など、axios 以外の応答なしのエラーは含めない
 * （送り直すと別のセッションで登録し得る）。
 */
export function isOutcomeUnknown(error: any): boolean {
  const response = error?.response;
  if (!response) return Boolean(error?.isAxiosError);
  // PR #104 独立照合。招待メールを送れなかった 502（code: email_not_sent）は、サーバーが
  // 登録を取り消したうえで返す「結果が分かった失敗」。送り直しても直らない（打ち間違いの
  // メールなど）ので、送り直さずに理由を示す。code の無い 502（ゲートウェイ）は分からない。
  if (response.data?.code === 'email_not_sent') return false;
  // RV-133。中継サーバーの 408 では、作成が確定したか分からない。同じ ID で確認する。
  if (response.status === 408) return true;
  if (response.status >= 500) return true;
  return response.status === 409 && response.data?.code === 'idempotency_in_progress';
}

const wait = (ms: number) =>
  ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();

export async function registerDriver(payload: { [key: string]: any }): Promise<RegisterOutcome> {
  const requestId = newRequestId();
  // PR #104 レビュー（4178136588）。冪等の範囲は利用者ごと。登録を始めたときのセッションを
  // 控え、1回目と送り直しの両方にその印（_sentWith）を付ける。印が今のセッションと違えば、
  // axiosInstance の interceptor が送らずに止める（SessionChangedError）。送り直しの待ちの
  // 間に別のアカウントでログインし直しても、新しいアカウントの会社に同じ運転者を作らない。
  const held = { session: getSessionIdentity(), generation: getSessionGeneration() };
  const send = () =>
    axiosInstance.post('/v1/drivers/', payload, {
      headers: { 'X-Request-ID': requestId },
      _sentWith: held,
    } as any);

  let last: unknown;
  try {
    return outcomeOf(await send());
  } catch (first) {
    if (!isOutcomeUnknown(first)) throw first;
    last = first;
  }

  // 結果が分からない失敗（応答なし・5xx）の送り直しは1回まで。処理中（409）の問い合わせ
  // 直しは別に数え、上限の回数まで続ける。
  let resends = 0;
  let polls = 0;
  for (;;) {
    if (isInProgress(last)) {
      if (polls >= registerTiming.maxInProgressPolls) throw new OutcomeUnknownError(last);
      polls += 1;
    } else {
      if (resends >= 1) {
        // EV-079。後片付け待ちが続いたなら、結果は分かっている（招待されていない登録が残る）。
        if (isCleanupPending(last)) throw new CleanupPendingError(last);
        throw new OutcomeUnknownError(last);
      }
      resends += 1;
    }
    await wait(registerTiming.retryDelayMs);
    if (getSessionIdentity() !== held.session) {
      // 送り直す前にセッションが変わっていた。1回目の結果は分からないまま。
      throw new OutcomeUnknownError(new Error('The session changed before resending'));
    }
    try {
      return outcomeOf(await send());
    } catch (error) {
      // 送り直しで元の要求の結果が分かった失敗（項目のエラーなど）だけ、そのまま返す。
      if (tellsOriginalOutcome(error)) throw error;
      // 冪等の処理に届く前に断られた（401・別の利用者への切り替えなど）なら、分からないまま。
      if (!isOutcomeUnknown(error)) throw new OutcomeUnknownError(error);
      last = error;
    }
  }
}

/** 同じ値の要求をサーバーが処理中（409 idempotency_in_progress）か。 */
export function isInProgress(error: any): boolean {
  const response = error?.response;
  return response?.status === 409 && response.data?.code === 'idempotency_in_progress';
}

/**
 * 1回目の結果が分からないまま送り直したときの失敗が、元の要求の結果を表すか。
 *
 * PR #104 レビュー（4178042382）。送り直しが冪等の処理に届く前に断られた場合（別の利用者
 * への切り替え・401・403・429・token の更新の失敗など）は、1回目がサーバーで処理された
 * かは分からない。確定した失敗として扱うと一覧を読み直さず、作られた運転者が見えない。
 * 分かったとみなすのは、冪等の処理を通った応答だけ。2xx のリプレイは成功として扱う。
 * - 409 idempotency_conflict
 * - 業務の 4xx（項目のエラーの 400）
 * - 502 email_not_sent（招待を送れず登録を取り消した）
 */
export function tellsOriginalOutcome(error: any): boolean {
  const response = error?.response;
  if (!response) return false;
  const code = response.data?.code;
  if (response.status === 409) return code === 'idempotency_conflict';
  if (code === 'email_not_sent') return true;
  return response.status === 400;
}
